import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  HapticSurface,
  Input,
  panelRow,
} from "@pax/ui";
import { useQuery } from "@tanstack/react-query";
import { Search, UserRound } from "lucide-react";
import { useState } from "react";
import { type ActAsTarget, personasQuery, userSearchQuery } from "../act-as";
import { AccountAvatar } from "./AccountAvatar";

export function ActAsControls({
  pending,
  onActAs,
  onFindUser,
}: {
  pending: boolean;
  onActAs: (target: ActAsTarget) => void;
  onFindUser: () => void;
}) {
  const personas = useQuery(personasQuery);

  return (
    <>
      {personas.data?.map(persona => (
        <button
          key={persona.id}
          type="button"
          disabled={pending}
          onClick={() => onActAs(persona)}
          className={`${panelRow} disabled:pointer-events-none disabled:opacity-50`}
        >
          <AccountAvatar
            name={persona.name}
            size={24}
            fallback={persona.email}
            className="shrink-0"
          />
          <span className="min-w-0 flex-1 truncate">{persona.name}</span>
          <HapticSurface />
        </button>
      ))}
      <button
        type="button"
        disabled={pending}
        onClick={onFindUser}
        className={`${panelRow} disabled:pointer-events-none disabled:opacity-50`}
      >
        <Search aria-hidden className="size-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate">Find a user…</span>
        <HapticSurface />
      </button>
      {personas.isError ? (
        <p role="alert" className="px-2.5 py-1.5 text-xs text-destructive">
          {personas.error.message}
        </p>
      ) : null}
    </>
  );
}

export function FindUserDialog({
  open,
  pending,
  onOpenChange,
  onActAs,
  error,
}: {
  open: boolean;
  pending: boolean;
  onOpenChange: (open: boolean) => void;
  onActAs: (target: ActAsTarget) => void;
  error: string | null;
}) {
  const [term, setTerm] = useState("");
  const results = useQuery(userSearchQuery(term));

  return (
    <Dialog
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) setTerm("");
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Act as a user</DialogTitle>
          <DialogDescription>
            Search by email or name. You will see the app exactly as they do until you stop acting.
          </DialogDescription>
        </DialogHeader>

        <Input
          autoFocus
          type="search"
          placeholder="Email or name"
          value={term}
          onChange={event => setTerm(event.target.value)}
          aria-label="Search users"
          disabled={pending}
        />

        {error ? (
          <p role="alert" className="rounded-lg bg-destructive/10 px-3 py-2 text-destructive">
            {error}
          </p>
        ) : null}

        <SearchResults
          pending={pending}
          targets={results.data}
          loading={results.isFetching}
          error={results.error?.message}
          onActAs={onActAs}
        />
      </DialogContent>
    </Dialog>
  );
}

function SearchResults({
  pending,
  targets,
  loading,
  error,
  onActAs,
}: {
  pending: boolean;
  targets?: ActAsTarget[];
  loading: boolean;
  error?: string;
  onActAs: (target: ActAsTarget) => void;
}) {
  if (error) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {error}
      </p>
    );
  }
  if (loading && !targets) {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        Searching…
      </p>
    );
  }
  if (!targets) return null;
  if (targets.length === 0) {
    return <p className="text-sm text-muted-foreground">No users match.</p>;
  }
  return (
    <ul className="flex flex-col gap-1">
      {targets.map(target => (
        <li key={target.id}>
          <button
            type="button"
            disabled={pending}
            onClick={() => onActAs(target)}
            className="flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left text-sm transition hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
          >
            <AccountAvatar
              name={target.name}
              size={28}
              fallback={target.isAnonymous ? "G" : target.email}
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">
                {target.isAnonymous ? "Guest" : target.name}
              </span>
              <span className="block truncate text-xs text-muted-foreground">{target.email}</span>
            </span>
            <UserRound className="size-4 text-muted-foreground" aria-hidden />
          </button>
        </li>
      ))}
    </ul>
  );
}
